import { cap } from '../../State'
import { translations } from '../../translations'
import { Button } from '../Common/Button'
import { Flex } from '../Common/Flex'
import { Spinner } from '../Common/Spinner'
import { TextareaAuto } from '../Common/TextareaAuto'

const language = cap.language.state || 'IT'
const t = translations[language]

const handleKeypress = (send) => (e) => {
  if (e.code === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

/**
 * Icona invio compatta per il composer stile Gigi (SVG inline, nessuna dipendenza da tema esterni).
 * @returns {JSX.Element}
 */
const SendPlaneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a1 1 0 0 0-1.38 1.21L4.5 12l-2.48 7.19a1 1 0 0 0 1.38 1.21z" />
  </svg>
)

/**
 * Componente Composer per la gestione dell'input di messaggi e allegati.
 *
 * @param {Object} props Oggetto che contiene le proprietà passate al componente.
 * @param {Function} props.update Funzione per aggiornare lo stato del messaggio corrente.
 * @param {Function} props.send Funzione per inviare il messaggio e gli allegati.
 * @param {string} props.currentMessage Messaggio attualmente digitato dall'utente.
 * @param {boolean} props.sending Indica se un messaggio è in fase di invio.
 * @param {Object} props.composerStyle Stili CSS aggiuntivi per il componente.
 * @param {'default'|'gigi'} [props.variant] - Variante layout: `gigi` usa barra compatta stile form operatore.
 * @param {string} [props.placeholder] - Placeholder dell'area testo.
 * @param {string} [props.sendAriaLabel] - Etichetta accessibilità del pulsante invio (variante gigi).
 * @param {string} [props.className] - Classi CSS aggiuntive sul wrapper.
 * @returns {React.Component} Ritorna un componente React che rappresenta l'area di composizione dei messaggi.
 */
export const Composer = ({
  update,
  send,
  currentMessage,
  sending,
  composerStyle,
  variant = 'default',
  placeholder,
  sendAriaLabel,
  className
}) => {
  const isGigi = variant === 'gigi'
  const defaultPlaceholder = t.gfChatComposerPlaceholder

  return (
    <div
      className={[isGigi ? 'gf-chat-composer' : '', className].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        flexDirection: 'row',
        ...(isGigi
          ? {
              width: '100%',
              flex: 1,
              minWidth: 0,
              gap: 10,
              paddingRight: 0
            }
          : { width: '80%', paddingRight: 40 }),
        ...composerStyle
      }}
    >
      {isGigi ? (
        <div className="gf-chat-composer__field">
          <TextareaAuto
            placeholder={placeholder ?? defaultPlaceholder}
            rows={1}
            style={{
              paddingTop: 10,
              paddingBottom: 10,
              minHeight: 44,
              maxHeight: 160,
              height: 'auto',
              borderRadius: 10,
              fontSize: 13
            }}
            value={currentMessage}
            autoFocus
            onChange={(e) => update(e.target.value)}
            onKeyPress={handleKeypress(send)}
          />
        </div>
      ) : (
        <TextareaAuto
          placeholder={placeholder ?? defaultPlaceholder}
          style={{ paddingTop: 12, minHeight: 45 }}
          value={currentMessage}
          autoFocus
          onChange={(e) => update(e.target.value)}
          onKeyPress={handleKeypress(send)}
        />
      )}
      {sending ? (
        <Flex style={{ marginLeft: isGigi ? 0 : 10, height: 44, width: 44, flexShrink: 0 }}>
          <Spinner />
        </Flex>
      ) : isGigi ? (
        <button
          type="button"
          className="gf-chat-composer__send"
          onClick={send}
          aria-label={sendAriaLabel || t.gfInvia}
          disabled={!currentMessage?.trim()}
        >
          <SendPlaneIcon />
        </button>
      ) : (
        <Button
          icon="send"
          iconSize={36}
          onClick={send}
          style={{ marginLeft: 10, height: 44, width: 44, borderRadius: 22 }}
        />
      )}
    </div>
  )
}
